import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
    LayoutDashboard, CreditCard, Search, ClipboardList, Ticket,
    Share2, Check, LogOut,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { membersApi } from "@/lib/api/directory";
import { toast } from "sonner";

const NAV_ITEMS = [
    { to: "/member/dashboard", labelEn: "Dashboard",    labelAr: "لوحة التحكم",  Icon: LayoutDashboard, end: true },
    { to: "/member/card",      labelEn: "My Card",      labelAr: "بطاقتي",       Icon: CreditCard },
    { to: "/member/home",      labelEn: "Find Care",    labelAr: "ابحث عن رعاية", Icon: Search },
    { to: "/member/claims",    labelEn: "My Deals",     labelAr: "عروضي",         Icon: Ticket },
    { to: "/member/visits",    labelEn: "My Visits",    labelAr: "زياراتي",       Icon: ClipboardList },
];

const TIER_COLORS = {
    SILVER:   "bg-zinc-200 text-zinc-700",
    GOLD:     "bg-amber-100 text-amber-700",
    PLATINUM: "bg-sky-100 text-sky-700",
};

// ── Desktop sidebar ─────────────────────────────────────────────────────────
function DesktopSidebar({ member, lang, toggle, logout }) {
    const [copied, setCopied] = useState(false);

    const referralLink = member
        ? `${window.location.origin}/signup?ref=${member.member_code}`
        : "";

    const copyReferral = () => {
        if (!referralLink) return;
        navigator.clipboard.writeText(referralLink).then(() => {
            setCopied(true);
            toast.success(lang === "ar" ? "تم نسخ رابط الدعوة" : "Referral link copied!");
            setTimeout(() => setCopied(false), 2500);
        });
    };

    const tierLabel = member?.tier
        ? { SILVER: lang === "ar" ? "فضي" : "Silver", GOLD: lang === "ar" ? "ذهبي" : "Gold", PLATINUM: lang === "ar" ? "بلاتيني" : "Platinum" }[member.tier] ?? member.tier
        : null;

    return (
        <aside className="hidden md:flex flex-col sticky top-24 w-[220px] shrink-0 rounded-2xl border border-border bg-card overflow-hidden">

            {/* Member identity */}
            {member && (
                <div className="px-5 py-5 border-b border-border">
                    <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <span className="text-sm font-bold text-primary">
                                {(member.full_name || "M").charAt(0).toUpperCase()}
                            </span>
                        </div>
                        <div className="min-w-0">
                            <p className="text-sm font-semibold truncate">{member.full_name}</p>
                            {tierLabel && (
                                <span className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded-full mt-0.5 ${TIER_COLORS[member.tier] || TIER_COLORS.SILVER}`}>
                                    {tierLabel}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Nav */}
            <nav className="flex-1 px-3 py-4 space-y-0.5">
                {NAV_ITEMS.map(({ to, labelEn, labelAr, Icon, end }) => (
                    <NavLink
                        key={to}
                        to={to}
                        end={end}
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                                isActive
                                    ? "bg-primary/10 text-primary"
                                    : "text-muted-foreground hover:text-foreground hover:bg-accent"
                            }`
                        }
                    >
                        <Icon className="h-4 w-4 shrink-0" />
                        {lang === "ar" ? labelAr : labelEn}
                    </NavLink>
                ))}
            </nav>

            {/* Bottom actions */}
            <div className="px-3 py-4 border-t border-border space-y-1">
                <button
                    onClick={copyReferral}
                    className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                >
                    {copied ? <Check className="h-4 w-4 text-success shrink-0" /> : <Share2 className="h-4 w-4 shrink-0" />}
                    {lang === "ar" ? "دعوة صديق" : "Invite Friends"}
                </button>

                <button
                    onClick={logout}
                    className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm font-medium text-destructive/70 hover:text-destructive hover:bg-destructive/5 transition-colors"
                >
                    <LogOut className="h-4 w-4 shrink-0" />
                    {lang === "ar" ? "تسجيل الخروج" : "Sign Out"}
                </button>
            </div>
        </aside>
    );
}

// ── Mobile tab strip ─────────────────────────────────────────────────────────
function MobileTabStrip({ lang }) {
    return (
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-card border-t border-border flex items-center justify-around px-2 py-1 safe-area-inset-bottom">
            {NAV_ITEMS.map(({ to, labelEn, labelAr, Icon, end }) => (
                <NavLink
                    key={to}
                    to={to}
                    end={end}
                    className={({ isActive }) =>
                        `flex flex-col items-center gap-0.5 px-3 py-2 rounded-xl text-[10px] font-medium transition-colors ${
                            isActive ? "text-primary" : "text-muted-foreground"
                        }`
                    }
                >
                    <Icon className="h-5 w-5" />
                    {lang === "ar" ? labelAr : labelEn}
                </NavLink>
            ))}
        </nav>
    );
}

// ── Exported component ───────────────────────────────────────────────────────
export default function MemberSidebar() {
    const { logout } = useAuth();
    const { lang, toggle } = useI18n();
    const navigate = useNavigate();
    const [member, setMember] = useState(null);

    useEffect(() => {
        membersApi.me().then(setMember).catch(() => {});
    }, []);

    const handleLogout = async () => {
        await logout();
        navigate("/");
    };

    return (
        <>
            <DesktopSidebar member={member} lang={lang} toggle={toggle} logout={handleLogout} />
            <MobileTabStrip lang={lang} />
        </>
    );
}
